import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  PlusSquare,
  ClipboardCheck,
  Receipt,
  TrendingUp,
  FolderOpen,
  ArrowLeftRight,
  FileDown,
  FileUp,
  Users,
  UserCheck,
  LayoutDashboard,
  PenLine,
  Building2,
  Upload,
  HelpCircle,
  User as UserIcon,
  Settings,
  ChevronDown,
  LogOut,
} from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { useDossierStore } from "../../store/dossierStore";
import { comptaSectionFromPath, type SecondaryPanelKind } from "./nav/secondarySidebarNav";

type LucideIcon = React.ComponentType<{ className?: string; strokeWidth?: number }>;

function getInitials(user: User | null): string {
  if (!user) return "?";
  const meta = user.user_metadata as { full_name?: string } | undefined;
  const fullName = meta?.full_name?.trim();
  if (fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase().slice(0, 2);
    }
    return fullName.slice(0, 2).toUpperCase();
  }
  const email = user.email?.split("@")[0] || "";
  return (email.slice(0, 2) || "U").toUpperCase();
}

const menuCompta: { id: SecondaryPanelKind; label: string; icon: LucideIcon }[] = [
  { id: "saisie", label: "Saisie", icon: PlusSquare },
  { id: "revision", label: "Révision", icon: ClipboardCheck },
  { id: "fiscalite", label: "Fiscalité", icon: Receipt },
  { id: "etats", label: "États financiers", icon: TrendingUp },
  { id: "dossier", label: "Dossier client", icon: FolderOpen },
];

type GestionMainId = "dashboard" | "transactions" | "saisie-ops" | "factures-achat" | "factures-vente";
type GestionBasId = "fournisseurs" | "clients";
type GestionNavId = GestionMainId | GestionBasId;
type SystemeNavId = "compte" | "parametres" | "aide";

/** Chemins réels des routes (pas de /gestion/dashboard ni /gestion/transactions dans l’app). */
const menuGestion: { id: GestionMainId; label: string; icon: LucideIcon }[] = [
  { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "transactions", label: "Transactions", icon: ArrowLeftRight },
  { id: "saisie-ops", label: "Saisie des opérations", icon: PenLine },
  { id: "factures-achat", label: "Factures d'achat", icon: FileDown },
  { id: "factures-vente", label: "Factures de vente", icon: FileUp },
];

const menuGestionBas: { id: GestionBasId; label: string; icon: LucideIcon }[] = [
  { id: "fournisseurs", label: "Fournisseurs", icon: Users },
  { id: "clients", label: "Clients", icon: UserCheck },
];

const menuSysteme: { id: SystemeNavId; label: string; icon: LucideIcon }[] = [
  { id: "compte", label: "Mon compte", icon: UserIcon },
  { id: "parametres", label: "Paramètres", icon: Settings },
  { id: "aide", label: "Aide", icon: HelpCircle },
];

function navItemButtonClass(active: boolean) {
  return [
    "relative mb-px flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-[7px] text-left transition-colors",
    active ? "bg-orange-500/10" : "hover:bg-white/5",
  ].join(" ");
}

function navIconClass(active: boolean) {
  return ["h-4 w-4 shrink-0 text-white", active ? "text-orange-500 opacity-100" : "opacity-40"].join(" ");
}

function navLabelClass(active: boolean) {
  return ["text-[13px]", active ? "font-medium text-orange-500" : "text-white/45"].join(" ");
}

function ActiveBar({ visible }: { visible: boolean }) {
  if (!visible) return null;
  return (
    <span
      className="pointer-events-none absolute left-0 top-1/2 h-4 w-[2px] -translate-y-1/2 rounded-r-sm bg-orange-500"
      aria-hidden
    />
  );
}

export interface SidebarProps {
  activeMainTab: "gestion" | "compta";
  setActiveMainTab: (v: "gestion" | "compta") => void;
  secondaryPanel: SecondaryPanelKind | null;
  setSecondaryPanel: (v: SecondaryPanelKind | null) => void;
  toggleSecondary: (kind: SecondaryPanelKind) => void;
  isCabinetFirm: boolean;
  isOwner: boolean;
  user: User | null;
  onLogout: () => void;
  accesGestion: boolean;
  accesCompta: boolean;
  typeComptabilite?: "tresorerie" | "engagement" | null;
  showParametresEntrepriseNav: boolean;
}

export default function Sidebar({
  activeMainTab,
  setActiveMainTab,
  secondaryPanel,
  setSecondaryPanel,
  toggleSecondary,
  isCabinetFirm,
  isOwner,
  user,
  onLogout,
  accesGestion,
  accesCompta,
  typeComptabilite,
  showParametresEntrepriseNav,
}: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const activeDossier = useDossierStore((s) => s.activeDossier);
  const isTresorerie = typeComptabilite === "tresorerie";
  const canUseCompta = accesCompta && !isTresorerie;
  const isComptaMode = activeMainTab === "compta";

  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const activeComptaSection = comptaSectionFromPath(location.pathname);

  const dashboardPath = isCabinetFirm ? "/cabinet" : "/dashboard";

  function gestionItemActive(id: GestionNavId): boolean {
    const p = location.pathname;
    switch (id) {
      case "dashboard":
        return p === "/dashboard" || p === "/cabinet" || p.startsWith("/cabinet/");
      case "transactions":
        return p === "/transactions" || p.startsWith("/transactions/");
      case "saisie-ops":
        return p.startsWith("/gestion/transactions");
      case "factures-achat":
        return p.startsWith("/gestion/factures-achat") || secondaryPanel === "factures-achat";
      case "factures-vente":
        return (
          p.startsWith("/gestion/factures-vente") ||
          p.startsWith("/gestion/devis") ||
          secondaryPanel === "facturation"
        );
      case "fournisseurs":
        return p === "/gestion/fournisseurs" || p.startsWith("/gestion/fournisseurs/");
      case "clients":
        return p === "/gestion/clients" || p.startsWith("/gestion/clients/");
      default:
        return false;
    }
  }

  function onGestionItemClick(id: GestionNavId) {
    switch (id) {
      case "dashboard":
        setSecondaryPanel(null);
        navigate(dashboardPath);
        break;
      case "transactions":
        setSecondaryPanel(null);
        navigate("/transactions", { state: { navFrom: "gestion" } });
        break;
      case "saisie-ops":
        setSecondaryPanel(null);
        navigate("/gestion/transactions/saisie", { state: { navFrom: "gestion" } });
        break;
      case "factures-achat":
        setSecondaryPanel("factures-achat");
        navigate("/gestion/factures-achat");
        break;
      case "factures-vente":
        setSecondaryPanel("facturation");
        navigate("/gestion/factures-vente", { state: { navFrom: "gestion" } });
        break;
      case "fournisseurs":
        setSecondaryPanel(null);
        navigate("/gestion/fournisseurs");
        break;
      case "clients":
        setSecondaryPanel(null);
        navigate("/gestion/clients");
        break;
      default:
        break;
    }
  }

  function systemeItemActive(id: SystemeNavId): boolean {
    const p = location.pathname;
    if (id === "compte") return p.startsWith("/compte");
    if (id === "parametres") {
      return p.startsWith("/parametres");
    }
    return p === "/aide" || p.startsWith("/aide/");
  }

  function onSystemeItemClick(id: SystemeNavId) {
    if (id === "parametres") {
      setSecondaryPanel("parametres");
      if (!location.pathname.startsWith("/parametres")) {
        navigate("/parametres/entreprise?onglet=generaux");
      }
      return;
    }
    setSecondaryPanel(null);
    if (id === "compte") navigate("/compte");
    else if (id === "aide") navigate("/aide");
  }

  const displayName = user?.user_metadata?.full_name || user?.email?.split("@")[0] || "Utilisateur";
  const initials = getInitials(user);

  const visibleGestionMain = isTresorerie
    ? menuGestion.filter((m) => ["dashboard", "transactions", "saisie-ops"].includes(m.id))
    : menuGestion;

  const visibleGestionBas = isTresorerie ? [] : menuGestionBas;

  const visibleMenuSysteme = menuSysteme.filter(
    (item) => item.id !== "parametres" || (isOwner && showParametresEntrepriseNav)
  );

  const orgActive = location.pathname === "/cabinet/organisation";
  const exportActive = location.pathname === "/export/operations";

  return (
    <aside
      className="fixed left-0 top-0 z-50 flex h-screen w-[216px] shrink-0 flex-col p-3 shadow-sidebar"
      style={{ background: "linear-gradient(180deg, #1a2744 0%, #b03a6e 100%)" }}
    >
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-orange-500 text-sm font-bold text-white">
          W
        </div>
        <span className="truncate text-sm font-medium text-white/80">Woahda.</span>
      </div>

      {canUseCompta && accesGestion && (
        <div className="mb-4 rounded-md bg-[rgba(255,255,255,0.07)] p-[3px]">
          <div className="flex">
            <button
              type="button"
              className={[
                "flex-1 rounded py-2 text-sm transition-colors",
                activeMainTab === "gestion"
                  ? "bg-white font-medium text-slate-900"
                  : "bg-transparent font-normal text-white/40 hover:text-white/55",
              ].join(" ")}
              onClick={() => {
                setActiveMainTab("gestion");
                setSecondaryPanel(null);
                navigate(dashboardPath);
              }}
            >
              Gestion
            </button>
            <button
              type="button"
              className={[
                "flex-1 rounded py-2 text-sm transition-colors",
                activeMainTab === "compta"
                  ? "bg-white font-medium text-slate-900"
                  : "bg-transparent font-normal text-white/40 hover:text-white/55",
              ].join(" ")}
              onClick={() => {
                setActiveMainTab("compta");
                setSecondaryPanel(null);
                navigate("/compta/revision/balance");
              }}
            >
              Compta
            </button>
          </div>
        </div>
      )}

      {accesCompta && !accesGestion && canUseCompta && (
        <div className="mb-3 text-center text-xs font-medium text-white/45">Comptabilité</div>
      )}
      {!accesGestion && !accesCompta && (
        <div className="mb-3 rounded-md border border-white/20 bg-white/5 px-2 py-2 text-center text-xs font-semibold text-white/90">
          Accès limité
        </div>
      )}

      <nav className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden pr-0.5">
        {isComptaMode && canUseCompta
          ? menuCompta.map((item) => {
              const active = activeComptaSection === item.id;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => toggleSecondary(item.id)}
                  className={navItemButtonClass(active)}
                >
                  <ActiveBar visible={active} />
                  <Icon className={navIconClass(active)} strokeWidth={2} />
                  <span className={navLabelClass(active)}>{item.label}</span>
                </button>
              );
            })
          : (
            <>
              {visibleGestionMain.map((item) => {
                const active = gestionItemActive(item.id);
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onGestionItemClick(item.id)}
                    className={navItemButtonClass(active)}
                  >
                    <ActiveBar visible={active} />
                    <Icon className={navIconClass(active)} strokeWidth={2} />
                    <span className={navLabelClass(active)}>{item.label}</span>
                  </button>
                );
              })}
            </>
          )}

        {isCabinetFirm && isOwner && (
          <>
            <div className="my-3 h-px bg-white/[0.06]" />
            <button
              type="button"
              onClick={() => {
                setSecondaryPanel(null);
                navigate("/cabinet/organisation");
              }}
              className={navItemButtonClass(orgActive)}
            >
              <ActiveBar visible={orgActive} />
              <Building2 className={navIconClass(orgActive)} strokeWidth={2} />
              <span className={navLabelClass(orgActive)}>Organisation du cabinet</span>
            </button>
          </>
        )}

        {isCabinetFirm && (
          <button
            type="button"
            onClick={() => {
              setSecondaryPanel(null);
              navigate("/export/operations");
            }}
            className={navItemButtonClass(exportActive)}
          >
            <ActiveBar visible={exportActive} />
            <Upload className={navIconClass(exportActive)} strokeWidth={2} />
            <span className={navLabelClass(exportActive)}>Exporter les opérations</span>
          </button>
        )}
      </nav>

      {!isComptaMode && visibleGestionBas.length > 0 && (
        <div className="mt-2 shrink-0">
          <div className="mb-1 mt-5 px-2 text-[10px] font-semibold uppercase tracking-widest text-white/20">
            Tiers
          </div>
          <div className="space-y-0">
            {visibleGestionBas.map((item) => {
              const active = gestionItemActive(item.id);
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => onGestionItemClick(item.id)}
                  className={navItemButtonClass(active)}
                >
                  <ActiveBar visible={active} />
                  <Icon className={navIconClass(active)} strokeWidth={2} />
                  <span className={navLabelClass(active)}>{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div className="mt-2 shrink-0 border-t border-white/[0.06] pt-3">
        <div className="space-y-0">
          {visibleMenuSysteme.map((item) => {
            const active = systemeItemActive(item.id);
            const Icon = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSystemeItemClick(item.id)}
                className={navItemButtonClass(active)}
              >
                <ActiveBar visible={active} />
                <Icon className={navIconClass(active)} strokeWidth={2} />
                <span className={navLabelClass(active)}>{item.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div ref={userMenuRef} className="relative z-[60] mt-2 shrink-0">
        <button
          type="button"
          onClick={() => setUserMenuOpen((o) => !o)}
          className="flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-[7px] text-left transition-colors hover:bg-white/5"
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-orange-500 text-[10px] font-bold text-white">
            {initials}
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[12px] font-medium text-white/70">{displayName}</div>
            <div className="truncate text-[10px] text-white/28">{activeDossier?.nom ?? "—"}</div>
          </div>
          <ChevronDown
            className={["h-4 w-4 shrink-0 text-white/40 transition-transform", userMenuOpen ? "rotate-180" : ""].join(
              " "
            )}
            strokeWidth={2}
          />
        </button>
        {userMenuOpen && (
          <>
            <div className="fixed inset-0 z-[58]" aria-hidden onClick={() => setUserMenuOpen(false)} />
            <div className="absolute bottom-full left-0 right-0 z-[59] mb-2 overflow-hidden rounded-lg border border-border-base bg-white py-1 shadow-sm">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors duration-150 hover:bg-night-soft hover:text-slate-900"
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate("/compte");
                }}
              >
                <UserIcon className="h-4 w-4 text-slate-500" />
                Mon compte
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  setUserMenuOpen(false);
                  onLogout();
                }}
              >
                <LogOut className="h-4 w-4" />
                Déconnexion
              </button>
            </div>
          </>
        )}
      </div>
    </aside>
  );
}
